import { Injectable, computed, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    darkTheme?: boolean;
    menuMode?: string;
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

/** Onde os filhos de um grupo do menu lateral abrem: abaixo do pai (acordeão) ou num painel ao lado. */
export type ModoSubmenu = 'abaixo' | 'lateral';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    _config: layoutConfig = {
        preset: 'Lara',
        primary: 'blue',
        surface: 'slate',
        darkTheme: false,
        menuMode: 'static'
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    layoutConfig = signal<layoutConfig>(this._config);

    layoutState = signal<LayoutState>(this._state);

    private readonly THEME_STORAGE_KEY = 'app-theme-dark';

    private readonly SUBMENU_STORAGE_KEY = 'app-menu-submenu';

    modoSubmenu = signal<ModoSubmenu>(this.carregarModoSubmenu());

    private configUpdate = new Subject<layoutConfig>();

    private overlayOpen = new Subject<any>();

    private menuSource = new Subject<MenuChangeEvent>();

    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();

    resetSource$ = this.resetSource.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    getPrimary = computed(() => this.layoutConfig().primary);

    getSurface = computed(() => this.layoutConfig().surface);

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    transitionComplete = signal<boolean>(false);

    private initialized = false;
    private themeInitialized = false;

    constructor() {
        this.initializeTheme();

        effect(() => {
            const config = this.layoutConfig();
            if (config) {
                this.onConfigUpdate();
            }
        });

        effect(() => {
            const config = this.layoutConfig();

            if (!this.themeInitialized || !config) {
                return;
            }

            if (!this.initialized) {
                this.initialized = true;
                return;
            }

            this.saveThemeToStorage(config.darkTheme || false);
            this.handleDarkModeTransition(config);
        });
    }

    private initializeTheme(): void {
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            this.loadAndApplyTheme();
        } else {
            setTimeout(() => {
                this.loadAndApplyTheme();
            }, 0);
        }
    }

    private loadAndApplyTheme(): void {
        const savedTheme = this.loadThemeFromStorage();

        this._config.darkTheme = savedTheme;
        this.layoutConfig.set({ ...this._config });

        this.applyThemeToDOM(savedTheme);

        this.themeInitialized = true;

    }

    private loadThemeFromStorage(): boolean {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
                const isDark = savedTheme === 'true';
                return isDark;
            }
        } catch (error) {
            console.warn('Erro ao carregar tema do localStorage:', error);
        }
        return false;
    }

    private saveThemeToStorage(isDark: boolean): void {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.setItem(this.THEME_STORAGE_KEY, isDark.toString());
                localStorage.getItem(this.THEME_STORAGE_KEY);
            }
        } catch (error) {
            console.warn('Erro ao salvar tema no localStorage:', error);
        }
    }

    private carregarModoSubmenu(): ModoSubmenu {
        try {
            return localStorage.getItem(this.SUBMENU_STORAGE_KEY) === 'abaixo' ? 'abaixo' : 'lateral';
        } catch {
            return 'lateral';
        }
    }

    definirModoSubmenu(modo: ModoSubmenu): void {
        this.modoSubmenu.set(modo);
        try {
            localStorage.setItem(this.SUBMENU_STORAGE_KEY, modo);
        } catch (error) {
            console.warn('Erro ao salvar o modo do submenu no localStorage:', error);
        }
    }

    private applyThemeToDOM(isDark: boolean): void {
        if (typeof document !== 'undefined') {
            if (isDark) {
                document.documentElement.classList.add('app-dark');
            } else {
                document.documentElement.classList.remove('app-dark');
            }

            document.documentElement.classList.contains('app-dark');

        }
    }

    public toggleTheme(): void {
        const currentTheme = this.layoutConfig().darkTheme;
        const newTheme = !currentTheme;

        console.log('Alternando tema de', currentTheme ? 'dark' : 'light', 'para', newTheme ? 'dark' : 'light');

        this.layoutConfig.update((state) => ({
            ...state,
            darkTheme: newTheme
        }));
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: layoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    toggleDarkMode(config?: layoutConfig): void {
        const _config = config || this.layoutConfig();
        this.applyThemeToDOM(_config.darkTheme || false);
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }
}
